"use client";

import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Printer,
  ChevronRight,
  Receipt,
  UtensilsCrossed,
  Settings,
  ArrowLeft,
  Eye,
} from "lucide-react";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PrintProductionOrder } from "./PrintProductionOrder";
import { PrintReceipt } from "./PrintReceipt";
import type { ProductionReport } from "@/interfaces/production-report";
import type { ReceiptReport } from "@/interfaces/receipt-report";
import { useAppManager } from "@/context/AppManagerContext";
import { formatCep, formatDddPhone, formatDocument } from "@/util/formatters";

interface PrintOptionsProps {
  productionReport: ProductionReport;
  receiptReport: ReceiptReport;
  onClose?: () => void;
}

type PrintType = "production" | "receipt";

export function PrintOptions({
  productionReport,
  receiptReport,
  onClose,
}: PrintOptionsProps) {
  const [selectedPrintType, setSelectedPrintType] = useState<PrintType | null>(
    null
  );
  const [step, setStep] = useState<"select" | "configure" | "preview">(
    "select"
  );
  const [currentTab, setCurrentTab] = useState<"settings" | "preview">(
    "preview"
  );
  const productionPreviewRef = useRef<HTMLDivElement>(null);
  const receiptPreviewRef = useRef<HTMLDivElement>(null);
  const { currentOrganization } = useAppManager();

  // Enhance reports with organization data
  useEffect(() => {
    if (!currentOrganization) return;

    const formattedAddress = currentOrganization.address
      ? `${currentOrganization.address.line1}, ${currentOrganization.address.number}${
          currentOrganization.address.line2
            ? ` - ${currentOrganization.address.line2}`
            : ""
        } - ${currentOrganization.address.neighborhood}, ${currentOrganization.address.city}${
          formatCep(currentOrganization.address.zipCode)
            ? ` - CEP: ${formatCep(currentOrganization.address.zipCode)}`
            : ""
        }`
      : undefined;

    const formattedPhone = currentOrganization.phoneNumber
      ? formatDddPhone(
          currentOrganization.ddd,
          currentOrganization.phoneNumber,
          currentOrganization.internationalCode
        )
      : "";

    const companyInfo = {
      name: currentOrganization.name,
      document: formatDocument(currentOrganization.document) || undefined,
      address: formattedAddress || undefined,
      phone: formattedPhone || undefined,
      logo: currentOrganization.file?.url,
    };

    if (productionReport) {
      productionReport.companyInfo = { ...companyInfo };
    }

    if (receiptReport) {
      receiptReport.companyInfo = { ...companyInfo };
    }
  }, [currentOrganization, productionReport, receiptReport]);

  const handleSelectPrintType = (type: PrintType) => {
    setSelectedPrintType(type);
    setStep("configure");
  };

  const handleBack = () => {
    if (step === "configure") {
      setStep("select");
      setSelectedPrintType(null);
    } else if (step === "preview") {
      setStep("configure");
    }
  };

  const handleTabChange = (value: string) => {
    setCurrentTab(value as "settings" | "preview");
  };

  const handlePrint = () => {
    try {
      console.log("Print button clicked for type:", selectedPrintType);

      // Get the content to print based on the selected type
      const contentRef =
        selectedPrintType === "production"
          ? productionPreviewRef
          : receiptPreviewRef;
      const contentElement = contentRef.current;

      if (!contentElement) {
        console.error("Conteúdo para impressão não encontrado");
        return;
      }

      console.log("Content found:", contentElement);

      // Create a new window for printing
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        console.error("Não foi possível abrir a janela de impressão");
        alert("Por favor, permita pop-ups para imprimir");
        return;
      }

      // Write the content to the new window
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Impressão</title>
            <style>
              @page { size: auto; margin: 10mm; }
              body { font-family: sans-serif; }
              hr { border: none; border-top: 1px dashed #000; }
              .text-xs { font-size: 12px; }
              .text-sm { font-size: 14px; }
              .text-base { font-size: 16px; }
              .text-lg { font-size: 18px; }
              .text-xl { font-size: 20px; }
              .text-2xl { font-size: 24px; }
              .font-bold { font-weight: bold; }
              .text-center { text-align: center; }
              .mb-1 { margin-bottom: 0.25rem; }
              .mb-2 { margin-bottom: 0.5rem; }
              .mb-4 { margin-bottom: 1rem; }
              .mt-2 { margin-top: 0.5rem; }
              .mt-4 { margin-top: 1rem; }
              .flex { display: flex; }
              .justify-between { justify-content: space-between; }
              .items-center { align-items: center; }
              .w-full { width: 100%; }
              table { width: 100%; border-collapse: collapse; }
              th, td { padding: 4px; }
              .border-b { border-bottom: 1px solid #eee; }
              .text-right { text-align: right; }
              .text-center { text-align: center; }
              .text-left { text-align: left; }
              .py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
              .pt-1 { padding-top: 0.25rem; }
              .mt-1 { margin-top: 0.25rem; }
              .mt-3 { margin-top: 0.75rem; }
            </style>
          </head>
          <body>
            ${contentElement.innerHTML}
          </body>
        </html>
      `);

      // Close the document for writing
      printWindow.document.close();

      // Wait for resources to load then print
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();

          // Close the dialog after printing
          if (onClose) {
            onClose();
          }
        };
      };

      // Fallback if onload doesn't trigger
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 1000);
    } catch (error) {
      console.error("Erro ao imprimir:", error);
      alert("Ocorreu um erro ao tentar imprimir. Por favor, tente novamente.");
    }
  };

  // Render the selection screen
  if (step === "select") {
    return (
      <div className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Selecione o tipo de impressão</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2">
          <Card
            className="cursor-pointer hover:border-[#EE7B11] transition-colors"
            onClick={() => handleSelectPrintType("production")}
          >
            <CardContent className="p-6 flex flex-col items-center text-center">
              <UtensilsCrossed className="h-12 w-12 text-[#EE7B11] mb-4" />
              <h3 className="text-lg font-medium mb-2">
                Impressão para Produção
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Imprima um pedido formatado para produção com detalhes dos itens
                e observações.
              </p>
              <Button
                variant="outline"
                className="mt-auto border-[#EE7B11] text-[#EE7B11] hover:bg-orange-50"
              >
                Selecionar <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:border-[#EE7B11] transition-colors"
            onClick={() => handleSelectPrintType("receipt")}
          >
            <CardContent className="p-6 flex flex-col items-center text-center">
              <Receipt className="h-12 w-12 text-[#EE7B11] mb-4" />
              <h3 className="text-lg font-medium mb-2">
                Comprovante para Cliente
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Imprima um comprovante detalhado para o cliente com valores,
                itens e informações de pagamento.
              </p>
              <Button
                variant="outline"
                className="mt-auto border-[#EE7B11] text-[#EE7B11] hover:bg-orange-50"
              >
                Selecionar <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (step === "configure" && selectedPrintType === "production") {
    return (
      <div className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-8 w-8 mr-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <DialogTitle>Configurar impressão para produção</DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          <Tabs
            value={currentTab}
            onValueChange={handleTabChange}
            className="flex-1 flex flex-col"
          >
            <TabsList className="mx-auto mb-4">
              <TabsTrigger value="settings" className="flex items-center gap-1">
                <Settings className="h-4 w-4" /> Configurações
              </TabsTrigger>
              <TabsTrigger value="preview" className="flex items-center gap-1">
                Pré-visualização
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-auto">
              {currentTab === "settings" && (
                <div className="p-4">
                  <PrintProductionOrder
                    report={productionReport}
                    showControls={false}
                    configOnly
                  />

                  <div className="flex justify-end mt-6">
                    <Button
                      onClick={() => setCurrentTab("preview")}
                      className="bg-gray-700 hover:bg-gray-800 text-white"
                    >
                      <Eye className="h-4 w-4 mr-2" /> Visualizar e Imprimir
                    </Button>
                  </div>
                </div>
              )}

              {currentTab === "preview" && (
                <div className="p-4" ref={productionPreviewRef}>
                  <PrintProductionOrder
                    report={productionReport}
                    showControls={false}
                    previewOnly
                  />
                </div>
              )}
            </div>
          </Tabs>

          {/* Sticky print button that's always visible */}
          {currentTab === "preview" && (
            <div className="sticky bottom-0 left-0 right-0 bg-white border-t p-4 mt-auto">
              <div className="flex justify-end">
                <Button
                  onClick={handlePrint}
                  className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
                >
                  <Printer className="h-4 w-4 mr-2" /> Imprimir Agora
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render the receipt configuration/preview
  if (step === "configure" && selectedPrintType === "receipt") {
    return (
      <div className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-8 w-8 mr-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <DialogTitle>Configurar comprovante</DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          <Tabs
            value={currentTab}
            onValueChange={handleTabChange}
            className="flex-1 flex flex-col"
          >
            <TabsList className="mx-auto mb-4">
              <TabsTrigger value="settings" className="flex items-center gap-1">
                <Settings className="h-4 w-4" /> Configurações
              </TabsTrigger>
              <TabsTrigger value="preview" className="flex items-center gap-1">
                Pré-visualização
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-auto">
              {currentTab === "settings" && (
                <div className="p-4">
                  <PrintReceipt
                    report={receiptReport}
                    showControls={false}
                    configOnly
                  />

                  <div className="flex justify-end mt-6">
                    <Button
                      onClick={() => setCurrentTab("preview")}
                      className="bg-gray-700 hover:bg-gray-800 text-white"
                    >
                      <Eye className="h-4 w-4 mr-2" /> Visualizar e Imprimir
                    </Button>
                  </div>
                </div>
              )}

              {currentTab === "preview" && (
                <div className="p-4" ref={receiptPreviewRef}>
                  <PrintReceipt
                    report={receiptReport}
                    showControls={false}
                    previewOnly
                  />
                </div>
              )}
            </div>
          </Tabs>

          {/* Sticky print button that's always visible */}
          {currentTab === "preview" && (
            <div className="sticky bottom-0 left-0 right-0 bg-white border-t p-4 mt-auto">
              <div className="flex justify-end">
                <Button
                  onClick={handlePrint}
                  className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
                >
                  <Printer className="h-4 w-4 mr-2" /> Imprimir Agora
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Fallback
  return <div>Selecione um tipo de impressão</div>;
}
